import { useEffect, useId, useRef, useState, type ReactNode } from "react"
import { ChevronDown } from "lucide-react"
import { Button, type ButtonProps } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export interface MenuItem {
  label: ReactNode
  description?: ReactNode
  icon?: ReactNode
  onSelect: () => void
  disabled?: boolean
}

/** Botão com menu suspenso. Fecha com Esc, clique fora ou ao escolher; setas navegam entre itens. */
export function MenuButton({ label, ariaLabel, icon, items, variant = "outline", size, disabled, align = "end", chevron = true, className, labelClassName }: {
  label?: ReactNode
  ariaLabel?: string
  icon?: ReactNode
  items: MenuItem[]
  variant?: ButtonProps["variant"]
  size?: ButtonProps["size"]
  disabled?: boolean
  align?: "start" | "end"
  chevron?: boolean
  className?: string
  labelClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const menuId = useId()

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (!rootRef.current?.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); rootRef.current?.querySelector<HTMLButtonElement>("button")?.focus() }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault()
        const items = [...(rootRef.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]:not([disabled])") ?? [])]
        const i = items.indexOf(document.activeElement as HTMLButtonElement)
        const next = e.key === "ArrowDown" ? (i + 1) % items.length : (i - 1 + items.length) % items.length
        items[next]?.focus()
      }
    }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    const t = setTimeout(() => rootRef.current?.querySelector<HTMLButtonElement>("[role=menuitem]:not([disabled])")?.focus(), 0)
    return () => { clearTimeout(t); document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey) }
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen(v => !v)}
        className={className}
      >
        {icon}
        {label && <span className={labelClassName}>{label}</span>}
        {chevron && <ChevronDown className="h-3.5 w-3.5 opacity-60" />}
      </Button>
      {open && (
        <div
          id={menuId}
          role="menu"
          className={cn(
            "absolute z-30 mt-1 min-w-[220px] rounded-lg border bg-card shadow-overlay py-1",
            align === "end" ? "right-0" : "left-0",
          )}
        >
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => { setOpen(false); item.onSelect() }}
              className="w-full text-left px-3 py-2 text-[14px] flex items-start gap-2.5 hover:bg-inset focus:bg-inset focus:outline-none disabled:opacity-50 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:mt-0.5 [&_svg]:shrink-0 [&_svg]:text-muted-foreground"
            >
              {item.icon}
              <span>
                <span className="block">{item.label}</span>
                {item.description && <span className="block text-[12px] text-muted-foreground">{item.description}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
